import cx from 'clsx';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import type { LocaleCode } from '@repo/shared-config/app/constants/locales';
import { IconIsometricFlag } from '#assets/icons/icon-isometric-flag';
import type { CountryCode } from '../../constants/countries';
import styles from './flag.module.css';

type BaseFlagProps = ComponentPropsWithoutRef<'span'>;

interface SquareFlagProps extends BaseFlagProps {
  variant?: 'square';
  code: CountryCode;
  rounded?: boolean;
  width?: number;
}

interface IsometricFlagProps extends BaseFlagProps {
  variant: 'isometric';
  locale: LocaleCode;
}

type FlagProps = SquareFlagProps | IsometricFlagProps;

function isIsometric(props: FlagProps): props is IsometricFlagProps {
  return props.variant === 'isometric';
}

export function Flag(props: FlagProps): ReactNode {
  if (isIsometric(props)) {
    const { className, locale, ...rest } = props;

    return (
      <span aria-label={locale} className={className} data-test-flag="" role="img" {...rest}>
        <IconIsometricFlag locale={locale} />
      </span>
    );
  }

  const { className, code, rounded = true, width = 16, ...rest } = props;

  return (
    <span
      aria-label={code}
      className={cx(className, styles.flag, styles[code.toLowerCase()], rounded && styles.rounded)}
      data-test-flag=""
      role="img"
      style={{ height: 0.75 * width, width }}
      {...rest}
    />
  );
}
